import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABIMDRANCxIQDhAUExIVGywdGxgYGzYnKSAsQDlEQz85Pj1HUGZXR0thTT0+WXlaYWltcnNyRVV9hnxvhWZwcm7/2wBDARMUFBsXGzQdHTRuST5Jbm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm7/wAARCAB4AFwDASIAAhEBAxEB/8QAGwAAAgMBAQEAAAAAAAAAAAAAAwUABAYCAQf/xAA3EAACAQMCBAQEAwcFAQAAAAABAgMABBESIQUxQVETImFxBhQyQoGRoRUWUrHB0fAjM2JjcuH/xAAYAQADAQEAAAAAAAAAAAAAAAABAgMABP/EAB4RAAICAwEBAQEAAAAAAAAAAAABAhEDEiExQRNh/9oADAMBAAIRAxEAPwB2g3oqg16trJ/DXXgOoyVNUJ2eqhNFVCBk7ChxqSwAJ3qvxeRjbmGByMfW/Mj0HrSSdDxVnF5xuyszpZy79lql+9MDNj5dtPfUKQ3VmUYlsrn8SaW3CtGMgAD1pjG9teK2l4DobSw5h9qM+k9Aa+d2jN8xg75FaThPEntZhDc+eFu53WihWhyyIfsFRbZX2WLPtRZCrHMa7HlXviTFcA6R6bURTxeHR83CqPeifJ2Y7n8aEF38zZPpRBGxG0bEUoT1XcfcaIHcjdjRRCh+6p4S6sZrWbUq3lwLW2BB88h0r6d6y8nE3eVsMcE4Ht3pj8S3GmYKp2jRvzO1ZdSWO3tSLrsr4qNAl3DMi+LGGx1711+wI79hPIGSP7EHX1Nc8G4eZSJJQQg6d60o+nApJTviKRhXWYy5sF4XM7OC4bdX9aXm5aSTOd62PEYY54njfBz0rFSRm2uHjcEFTzp8cr4xMka6jY8AufmrURs269fSnBjgX6mLntWL+F7rReFCdiSPwNawt6VVqyN0GNwsY/04wPegveShtziuGEj/AEqT7CvDazH7fzNCka2y0PeiIQM17EF0nIqaaVjJGX+IEGqV5G0LnGcb8+lZxXMkgCW5ZCcKNWOXc96efFEoDAdWY6R/Wh/DPhyW2HAY6idxUm6ReMdmH4NcXKYDrKsecaXOQPY1f4qs7rhNZA2wrac+5qxMUTw1yFUtRkkjaVlyrd8HNSb6WqkZq2e78dI/kI8E/UCcj1yaLf8ACmvuJQhcAFTrJ5ACtBMyIh0gUseUxyvO+0Kx6Xf+HJH9qtj7IjlVQF8lmvDuMx+Hp0lQ3lGBWraQCQMFGMcqyV1fLecR8SIER4CrnngVqFOUjJH2iuhJUcjk31hJJ36ZAoQYsMmjZaFlcAN0IoAYnJPPNZAsZQ4074qEgI59DVJCNZ1E4z0o6KZIpAp6HFI1Q0ZW6MDxqVrjiTMdkU6VHYUDgM5t7lomONXL3FOuKcPEc5Z9lbO9Z5oW1EnZl6juKXW1RbanZqLmRiFV4hKh3DasYqxbTMgCQ2qKW5nXv+JpZwa/WSPRd4yDjJ5GnL3dpBFlCo9B1rmap0dcZJx8ObpzDETIdZHRaylzcS8RvyhOmFGxgdcd+9PHaa9JcqREOnelCWsi3MkcaHxGbJ9KtiXSGZ8CWsQa8SNd/NitkjCNVDDPlpFwHhbiUzSghV5etPJCurOTgDpXT/Dkbs9aVU8wBJY5I7UHVkk4xk1xJNH/AMzQ/mYxsFb861C2MEiDnYmrEY8JT696TpeTK50GrdvcyS5EhzikZRJLw74jYLewaV2cbqelI5+Gx2dhJcXMedGwHXPY9x6itEhYsSdl7nalvxW6/u/Pvh8B0HfBGT+tDiYesyFtJqubhNtPlZAOQGKuRGGMhpTt2HM0n4Skj3cfPDKQTT34T0z8XYzKH0ozANvg9KX89pOyn6aQ4O4obi5hAgU2sJH+4485/wDK9Pc13Z2NtYSjwkyWPndjlm9zTSRhjc7nkOpqhczxxfVu38I/rTxXxE5Svsi7IABzqtIx+wBcDf1qsnFWJ86LgVYW+RxlYmI7+GSPzxTuLXpJTT8YPDKjuSjEDGGGfyqi2SeVMpLsImfCQ0H9o/8ARH+VbZr4bW/oFohHMqJKGLnG1NLZIoGwo1t1J6mktgAb/J+3JppYP49zNp+lGAz64qMy8UWJhlXkZjIVGdK8gKy/xDfSXNuYwBqkBRVG+F5n+VbC4C/LumQoKkeg2rK2doXkNzMNzsgPQf8A2pOTVMpCCYn4d8pDaGSV5BMCQAo5gj9KDaTS2Vw01vIUZlK5A6GtJb8Jt0lLrHrYnIB3x7Cubb4bLzGa9fYnPhof5n+1dKz78oi8GnruyxwLxZbQyhJDKzed5Xyrf52FMLq3jc6ZI8sdgw2/WjwRrDEI4VCRjkBRdGVwQCD3oOSu0BRfjKFvw6KHzSec+vIVdRCVHPfkPT1pbcXskXEILWMCQMckdQv+b00WQAEA5PU0sm31jRSXEVbyBZIypwD0NI3DIxUg5FPpjkGlc4JfI7U8GLNFa1JE0hAOThR+Jp9ZwrZQaTu7ku3oaVcLI1NM2PLso7mmHjZ3NTl1lI8R5xC4DeHblsGYnPsOf51XkQhlRASW5YpXxS518TcasCIBRv8Aif1NOLAFYxJKxaVxnfoKE8XjDDK1aouRQrGgGRq6miBc86GrZNdC4XJCYOObHkP71kviBKX1hSFRdTEKo6mq0l0C4jQEBubHnj0FAurlFbOouw6n/NqoC7JuASQNjVlj5bIPLcqQi4jxF34tLJC+JteI8dMHA/lWvtnZkAfAkAGoDlmvn02V4lNnmCd/xrW8Ev2uoSrg+LFtq6MP70slwpF9GcpwcUvLb1aklDHIqizeY+9GIJFSC6MaKOmok1dF0ChwalSs0ZMTOvj8fkDfQW8Q+owDT9LjHX3NSpWZkem8DAjPlA39fSq4vgBjlUqVTGiWV+FWa7zneqySFn1ZwF61KlPJ0icFbKF1AJr9PCBLybGtLYQpbQeEm+2Se571KlQl4dUTmV21HGrPcAVUJfJ3apUoxFkf/9k="; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
export const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

export const INR = n => "₹" + Number(n).toLocaleString("en-IN");
export const sc  = s => ({Online:T.green,Active:T.green,Stable:T.green,Resolved:T.green,
  Offline:T.red,Suspended:T.red,Locked:T.red,Critical:T.red,
  Degraded:T.accent,Acknowledged:T.accent,High:T.accent,
  Medium:T.orange,Beta:T.orange,Inactive:T.muted,
  Low:T.muted,Info:T.blue,Trial:T.purple,Open:T.red,
  "Challan Issued":T.blue,"Challan Pending":T.orange,
  "Court Referral":T.purple,Disputed:T.pink}[s]||T.muted);

/* -- PLANS (INR) -------------------------------------------------- */
export const PLANS = [
  {id:"starter",   name:"Starter",     color:T.green,  cameras:8,   users:5,  storage:"200 GB",retention:"30 days", sla:"99.0%",monthly:4999,  yearly:49999,  lifetime:149999,  jetson:false,custom:false,whatsapp:false,support:"Email Only",          api:"1 Lakh/mo"},
  {id:"pro",       name:"Professional",color:T.blue,   cameras:25,  users:15, storage:"1 TB",  retention:"90 days", sla:"99.5%",monthly:14999, yearly:149999, lifetime:449999,  jetson:true, custom:false,whatsapp:true, support:"Priority Phone",       api:"10 Lakh/mo"},
  {id:"enterprise",name:"Enterprise",  color:T.accent, cameras:100, users:50, storage:"5 TB",  retention:"1 Year",  sla:"99.9%",monthly:39999, yearly:399999, lifetime:999999,  jetson:true, custom:true, whatsapp:true, support:"24/7 Dedicated",       api:"Unlimited"},
  {id:"govt",      name:"Government",  color:T.purple, cameras:200, users:200,storage:"20 TB", retention:"3 Years", sla:"99.99%",monthly:59999,yearly:599999, lifetime:1499999, jetson:true, custom:true, whatsapp:true, support:"On-Site SLA Dedicated", api:"Unlimited"},
];

/* -- TENANTS ------------------------------------------------------ */
const TENANTS_INIT = []; // Live data from API — add tenants via UI

/* -- SITES -------------------------------------------------------- */
export const SITES_DATA = []; // Live data from API — populated via /api/cameras/sites/all

/* -- CAMERAS ------------------------------------------------------ */
const CAMERAS_INIT = []; // Live data from API

/* -- EVENTS ------------------------------------------------------- */
const EVENTS_INIT = []; // Live data from API

/* -- VIOLATIONS (TRAFFIC) ----------------------------------------- */
const VIOLS_INIT = []; // Live data from API

/* -- MODELS ------------------------------------------------------- */
const MODELS_INIT = []; // Loaded from API (/api/models) — seeded from schema.sql

/* -- RULES -------------------------------------------------------- */
const RULES_INIT = []; // Live data from API

/* -- USERS -------------------------------------------------------- */
const USERS_INIT = []; // Live data from API

/* -- JETSON NODES ------------------------------------------------- */
const JETSONS_INIT = []; // Live data from API

/* -- AUDIT LOG ---------------------------------------------------- */
const AUDIT_INIT = []; // Live data from API

/* -- REPORT TEMPLATES --------------------------------------------- */
const REPORTS_INIT = []; // Live data from API

/* -- CHART DATA --------------------------------------------------- */
export const alertHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  critical:Math.floor(Math.random()*4)+1,
  high:Math.floor(Math.random()*7)+2,
  medium:Math.floor(Math.random()*10)+3,
}));
export const trafficHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  violations:Math.floor(Math.random()*18)+2,
  vehicles:Math.floor(Math.random()*900)+200,
}));
export const co2Data = [
  {m:"Jan",co2:12.4,fuel:520,saved:28000,idle:310},
  {m:"Feb",co2:11.8,fuel:492,saved:31000,idle:285},
  {m:"Mar",co2:10.9,fuel:455,saved:35000,idle:260},
  {m:"Apr",co2:10.1,fuel:421,saved:38500,idle:238},
  {m:"May",co2:9.4, fuel:392,saved:42000,idle:212},
  {m:"Jun",co2:8.6, fuel:358,saved:47000,idle:190},
];
export const perfWeeks = [
  {w:"W1",ppe:95.5,anpr:97.8,fire:96.2,crowd:91.0},
  {w:"W2",ppe:96.2,anpr:98.1,fire:96.9,crowd:92.1},
  {w:"W3",ppe:97.1,anpr:98.6,fire:97.8,crowd:93.4},
  {w:"W4",ppe:97.8,anpr:99.1,fire:98.5,crowd:94.1},
];
export const radarData = [
  {s:"Compliance",v:94},{s:"Response",v:88},{s:"Coverage",v:92},
  {s:"Uptime",v:99},{s:"Accuracy",v:97},{s:"Cost Eff.",v:83},
];

/* -- AI AGENT RESPONSES ------------------------------------------- */
const AI_RESPONSES = {
  "last week":    "Last week (Jun 24-30), the platform detected <b>1,847 events</b> across all tenants.<br>🔴 <b>Critical:</b> Tata Motors CAM-006 (Welding Bay) -- 14 PPE violations in 5 days. Trend worsening -- immediate safety audit recommended.<br>🟡 <b>Shivajinagar Junction</b> -- 312 signal violations, 22% above weekly average.<br>🔒 <b>MSRTC Depot</b> auto-suspended -- storage quota breach (92%).<br>✅ <b>Uptime:</b> 99.4% platform-wide.",
  "predict":      "⚠️ <b>Next 7-Day Risk Forecast (AI Analysis):</b><br><b>1. J-004 Edge Node</b> -> GPU at 94%, RAM at 94% -- thermal degradation expected within 36-48 hours. <i>Action: Reboot + thermal paste replacement</i>.<br><b>2. Monsoon forecast Pune</b> (Jul 5-12) -> Historical data shows +38% accident spike. <i>Action: Enable fog-mode ANPR, raise alert sensitivity on all traffic cameras</i>.<br><b>3. Infosys storage at 88%</b> -> Quota breach in ~12 days. <i>Action: Upgrade to 2 TB plan</i>.",
  "violation":    "🔴 <b>Top 5 Violation Hotspots -- Last 30 Days:</b><br>1. Shivajinagar Junction (CAM-001/002) -- 1,247 violations - 61% signal jumps<br>2. FC Road Stretch (CAM-003) -- 892 violations - 74% overspeeding<br>3. Kothrud Junction -- 614 violations - wrong way + no helmet<br>4. Baner Road -- 521 violations - triple riding<br>5. Warje Bridge -- 398 violations - overspeeding<br><br>💰 Total fines generated: <b>₹14.7 Lakh</b> this month.",
  "ppe":          "📊 <b>PPE Compliance -- Tata Motors (30-day trend):</b><br>Current rate: <b>73.2%</b> v from 81.4% in May.<br>* CAM-005 (Assembly Line A) = 68% of all violations<br>* Peak violation window: <b>7-9 AM</b> (shift change)<br>* CAM-006 degraded = underreporting estimated at ~20%<br>* Forklift zone violations rising +14% this week<br><b>Recommendation:</b> Repair CAM-006 urgently. Schedule mandatory PPE briefing for morning shift. Add zone-entry interlock.",
  "monsoon":      "🌧️ <b>Monsoon Preparedness Plan (Jul-Sep 2025):</b><br>1. Enable weather-adaptive PTZ on all outdoor cameras<br>2. Raise ANPR confidence threshold to 94% to reduce rain false-positives<br>3. Pre-position maintenance team at J-001, J-002 (waterproofing + cable sealing)<br>4. Escalate medium->high sensitivity at known flood-prone intersections<br>5. Schedule daily camera health checks for outdoor units<br>6. Historical: Jun-Aug = +38% incident spike -- send advisory to all tenant admins",
  "cost":         "💰 <b>Platform ROI Summary -- Year To Date:</b><br>* Fuel savings: <b>₹2.84 Lakh</b> across all monitored fleets<br>* CO2 avoided: <b>51.8 tonnes</b> (~ 860 trees planted equivalent)<br>* Traffic fine revenue generated: <b>₹14.7 Lakh</b> (Pune Smart City)<br>* Incident prevention value (insurance est.): <b>₹8.2 Lakh</b><br>* Platform subscription MRR: <b>₹1.58 Lakh</b><br>--------------------------<br>🏆 <b>Average tenant ROI: 3.2x investment</b>",
};
const CHAT_INIT = [{role:"ai",msg:"Namaste! 🙏 I'm <b>AURAVEX GLOBAL AI Copilot</b> -- your AI analyst with 30 days of platform context.<br>I can help with: incident analysis, predictive risk forecasts, compliance trends, cost savings, traffic patterns, and preventive action plans.<br>Type your question or pick a quick prompt ->",time:"now"}];
/* ================================================================
   ATOMIC COMPONENTS
================================================================ */

/* Badge */

export const Badge = ({text,color=T.blue,xs,sm}) => (
  <span style={{
    fontSize:xs?8:sm?10:11, fontWeight:700, letterSpacing:.7,
    padding:xs?"1px 6px":sm?"2px 8px":"3px 10px",
    borderRadius:3, background:color+"20", color,
    border:`1px solid ${color}38`, textTransform:"uppercase",
    fontFamily:T.mono, whiteSpace:"nowrap", display:"inline-block",
  }}>{text}</span>
);

/* Status dot */
export const Dot = ({s}) => {
  const c = sc(s);
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,fontWeight:600,color:c,whiteSpace:"nowrap"}}>
      <span style={{width:7,height:7,borderRadius:"50%",background:c,boxShadow:`0 0 5px ${c}66`,flexShrink:0}}/>
      {s}
    </span>
  );
};

/* KPI Card */
export const KPI = ({label,value,sub,color=T.blue,icon,trend}) => (
  <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 16px",position:"relative",overflow:"hidden"}}>
    <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${color},transparent)`}}/>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
      <span style={{fontSize:9,color:T.muted,letterSpacing:1.5,textTransform:"uppercase",fontWeight:700}}>{label}</span>
      {icon && <span style={{fontSize:16}}>{icon}</span>}
    </div>
    <div style={{fontSize:25,fontWeight:800,color,fontFamily:T.mono,lineHeight:1,marginBottom:4}}>{value}</div>
    {sub   && <div style={{fontSize:10,color:T.muted}}>{sub}</div>}
    {trend !== undefined && (
      <div style={{fontSize:10,color:trend>0?T.green:T.red,fontWeight:700,marginTop:2}}>
        {trend>0?"^ +":"v "}{Math.abs(trend)}% vs last month
      </div>
    )}
  </div>
);

/* Panel */
export const Panel = ({title,children,action,accent,style={}}) => (
  <div style={{background:T.card,border:`1px solid ${accent?accent+"38":T.border}`,borderRadius:10,overflow:"hidden",...style}}>
    {title && (
      <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.panel}}>
        <span style={{fontSize:11,fontWeight:700,color:T.text,letterSpacing:.2}}>{title}</span>
        {action}
      </div>
    )}
    <div style={{padding:14}}>{children}</div>
  </div>
);

/* Progress Bar */
export const Prog = ({val,max=100,color=T.blue,h=5}) => (
  <div style={{background:T.border,borderRadius:3,height:h,overflow:"hidden"}}>
    <div style={{
      width:`${Math.min(100,Math.max(0,val/max*100))}%`,
      height:"100%", borderRadius:3,
      background: val/max>.9?T.red:val/max>.75?T.accent:color,
      transition:"width .4s ease",
    }}/>
  </div>
);

/* Pill Filter */
export const Pill = ({label,active,onClick,color=T.blue}) => (
  <button onClick={onClick} style={{
    padding:"4px 13px",border:`1px solid ${active?color:T.border}`,borderRadius:20,
    cursor:"pointer",fontSize:10,fontWeight:700,letterSpacing:.3,
    background:active?color+"20":"transparent",color:active?color:T.muted,
    fontFamily:T.font, transition:"all .12s",
  }}>{label}</button>
);

/* Button */
export const BtnStyles = {
  primary: {background:`linear-gradient(135deg,${T.accentD},${T.accent})`,color:"#FFFFFF",border:"none",fontWeight:800},
  ghost:   {background:"transparent",  color:T.silver,  border:`1px solid ${T.border}`},
  blue:    {background:T.blueLo,       color:T.blue,    border:`1px solid ${T.blue}40`},
  danger:  {background:T.redLo,        color:T.red,     border:`1px solid ${T.red}40`},
  success: {background:T.greenLo,      color:T.green,   border:`1px solid ${T.green}40`},
  warn:    {background:T.orangeLo,     color:T.orange,  border:`1px solid ${T.orange}40`},
  purple:  {background:T.purpleLo,     color:T.purple,  border:`1px solid ${T.purple}40`},
  teal:    {background:T.tealLo,       color:T.teal,    border:`1px solid ${T.teal}40`},
  muted:   {background:T.faint,        color:T.muted,   border:`1px solid ${T.border}`},
};
export const Btn = ({children,v="primary",sm,lg,onClick,disabled,full,icon}) => (
  <button disabled={disabled} onClick={onClick} style={{
    ...BtnStyles[v]||BtnStyles.ghost,
    padding:sm?"3px 10px":lg?"10px 20px":"7px 15px",
    borderRadius:7, cursor:disabled?"not-allowed":"pointer",
    fontSize:sm?10:lg?13:12, fontWeight:BtnStyles[v]?.fontWeight||700,
    fontFamily:T.font, letterSpacing:.3, opacity:disabled?.5:1,
    width:full?"100%":"auto", display:"inline-flex", alignItems:"center",
    gap:4, justifyContent:"center", transition:"filter .12s", whiteSpace:"nowrap",
  }}
  onMouseEnter={e=>!disabled&&(e.currentTarget.style.filter="brightness(1.15)")}
  onMouseLeave={e=>(e.currentTarget.style.filter="brightness(1)")}
  >{icon&&<span>{icon}</span>}{children}</button>
);

/* Input field */
export const Inp = ({label,value,onChange,placeholder,type="text",sm}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label && <label style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{label}</label>}
    <input
      type={type} value={value}
      onChange={e=>onChange(e.target.value)}
      placeholder={placeholder}
      style={{background:T.panel,border:`1px solid ${T.hi}`,borderRadius:7,padding:sm?"6px 10px":"9px 12px",color:T.text,fontSize:12,fontFamily:T.font,outline:"none",width:"100%"}}
    />
  </div>
);

/* Select field */
export const Sel = ({label,value,onChange,options}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label && <label style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{label}</label>}
    <select
      value={value} onChange={e=>onChange(e.target.value)}
      style={{background:T.panel,border:`1px solid ${T.hi}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:T.font,outline:"none",width:"100%"}}
    >
      {options.map(o=>(
        <option key={typeof o==="object"?o.v:o} value={typeof o==="object"?o.v:o}>
          {typeof o==="object"?o.l:o}
        </option>
      ))}
    </select>
  </div>
);

/* Textarea */
export const Textarea = ({label,value,onChange,placeholder,rows=3}) => (
  <div style={{display:"flex",flexDirection:"column",gap:4}}>
    {label && <label style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{label}</label>}
    <textarea
      value={value} onChange={e=>onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{background:T.panel,border:`1px solid ${T.hi}`,borderRadius:7,padding:"9px 12px",color:T.text,fontSize:12,fontFamily:T.font,outline:"none",resize:"vertical",width:"100%"}}
    />
  </div>
);

/* Modal */
export const Modal = ({open,title,children,onClose,wide,xl}) => {
  if(!open) return null;
  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(15,25,50,.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
      onClick={e=>e.target===e.currentTarget&&onClose()}
    >
      <div style={{
        background:T.panel, border:`1px solid ${T.hi}`,
        borderRadius:14, width:xl?"92vw":wide?"820px":"540px",
        maxWidth:"96vw", maxHeight:"92vh",
        overflow:"hidden", display:"flex", flexDirection:"column",
        boxShadow:"0 8px 48px rgba(26,34,53,.22)",
      }}>
        <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bg,flexShrink:0}}>
          <span style={{fontSize:13,fontWeight:800,color:T.text}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18,lineHeight:1,padding:2}}>✕</button>
        </div>
        <div style={{padding:20,overflowY:"auto",flex:1}}>{children}</div>
      </div>
    </div>
  );
};

/* Toast notification */
export const Toast = ({msg,type="success"}) => {
  const c = type==="success"?T.green:type==="error"?T.red:T.accent;
  return (
    <div style={{
      position:"fixed",bottom:22,right:22,zIndex:2000,
      background:T.panel,border:`1px solid ${c}55`,borderLeft:`3px solid ${c}`,
      borderRadius:10,padding:"10px 18px",fontSize:12,fontWeight:600,
      display:"flex",alignItems:"center",gap:10,
      boxShadow:"0 4px 20px rgba(26,34,53,.15)",color:T.text,
      animation:"slideIn .25s ease",
    }}>
      <span style={{color:c,fontSize:16}}>{type==="success"?"✓":type==="error"?"✕":"⚠"}</span>{msg}
    </div>
  );
};

/* Data Table */
export const DataTable = ({cols,rows,onRow,dense,emptyMsg}) => (
  <div style={{overflowX:"auto"}}>
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:dense?10:11}}>
      <thead>
        <tr>{cols.map(c=>(
          <th key={c.k} style={{padding:dense?"5px 8px":"7px 10px",textAlign:"left",color:T.muted,fontWeight:700,fontSize:9,letterSpacing:1,textTransform:"uppercase",borderBottom:`1px solid ${T.border}`,whiteSpace:"nowrap"}}>{c.l}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.length===0 ? (
          <tr><td colSpan={cols.length} style={{padding:"24px",textAlign:"center",color:T.muted,fontSize:12}}>{emptyMsg||"No data found"}</td></tr>
        ) : rows.map((row,i)=>(
          <tr key={i}
            onClick={()=>onRow&&onRow(row)}
            style={{background:i%2===0?"transparent":T.faint,cursor:onRow?"pointer":"default",transition:"background .1s"}}
            onMouseEnter={e=>{if(onRow)e.currentTarget.style.background=T.hi}}
            onMouseLeave={e=>{e.currentTarget.style.background=i%2===0?"transparent":T.faint}}
          >
            {cols.map(c=>(
              <td key={c.k} style={{padding:dense?"5px 8px":"8px 10px",borderBottom:`1px solid ${T.faint}`,verticalAlign:"middle"}}>
                {c.r ? c.r(row[c.k],row) : row[c.k]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* Section header */
export const PageHeader = ({title,sub,children}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
    <div>
      <div style={{fontSize:17,fontWeight:800,color:T.text}}>{title}</div>
      {sub && <div style={{fontSize:11,color:T.muted,marginTop:2}}>{sub}</div>}
    </div>
    {children && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{children}</div>}
  </div>
);

/* Alert box */
export const AlertBox = ({type="warn",children}) => {
  const colors={warn:[T.accent,T.accentLo],error:[T.red,T.redLo],success:[T.green,T.greenLo],info:[T.blue,T.blueLo]};
  const [c,bg]=colors[type]||colors.warn;
  return(
    <div style={{background:bg,border:`1px solid ${c}40`,borderRadius:8,padding:"10px 14px",fontSize:11,color:c,lineHeight:1.6}}>
      {children}
    </div>
  );
};

/* Grid layout helper */
export const Grid = ({cols=2,gap=12,children}) => (
  <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap}}>{children}</div>
);

/* Divider */
export const Divider = () => <div style={{height:1,background:T.border,margin:"4px 0"}}/>;
/* ================================================================
   PAGES -- Part B
   Dashboard - Tenants - Cameras - Events - Models -
   Traffic - Rules - Users - Subscriptions - CO2
================================================================ */

/* -- DASHBOARD ---------------------------------------------------- */

/* ==============================================================
   CAMERA LIVE VIEW MODAL -- full-screen maximized live feed
   ============================================================== */
export const CameraLiveModal = (props) => {
  const cam = props.cam;
  const onClose = props.onClose;
  const tickFromParent = props.tick;
  const [snapshot, setSnapshot] = useState(false);
  const [localTick, setLocalTick] = useState(0);
  useEffect(function() {
    const t = setInterval(function() { setLocalTick(function(p) { return p+1; }); }, 900);
    return function() { clearInterval(t); };
  }, []);
  const tick = tickFromParent !== undefined ? tickFromParent : localTick;
  if (!cam) return null;
  const c = {Online:T.green, Degraded:T.accent, Offline:T.red}[cam.status] || T.muted;
  const detections = cam.status === "Online" ? [
    {label:"Person",  conf:94, x:12, y:15, w:18, h:42, color:T.green},
    {label:"Vehicle", conf:98, x:55, y:30, w:25, h:30, color:T.blue},
    {label:(cam.models[0] ? cam.models[0].split(" ")[0] : "Object"), conf:87, x:35, y:20, w:20, h:35, color:T.accent},
  ] : [];

  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(5,10,20,.96)",display:"flex",flexDirection:"column"}}
      onClick={function(e){ if(e.target===e.currentTarget) onClose(); }}>

      {/* Top bar */}
      <div style={{height:52,background:T.bg,borderBottom:"1px solid "+T.border,display:"flex",alignItems:"center",padding:"0 20px",gap:16,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {cam.status==="Online" && (
            <div style={{display:"flex",alignItems:"center",gap:5,background:T.red+"18",border:"1px solid "+T.red+"44",borderRadius:20,padding:"3px 10px"}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:T.red,display:"block",boxShadow:"0 0 "+(tick%2===0?8:2)+"px "+T.red,transition:"box-shadow .8s"}}/>
              <span style={{fontSize:10,fontWeight:800,color:T.red,letterSpacing:1}}>LIVE</span>
            </div>
          )}
          <div>
            <div style={{fontSize:14,fontWeight:900,color:T.text}}>{cam.id} -- {cam.name}</div>
            <div style={{fontSize:10,color:T.muted,marginTop:1}}>{cam.brand} - {cam.type} - {cam.res} - {cam.fps}fps - {cam.ip}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginLeft:"auto"}}>
          <Badge text={cam.status} sm color={c}/>
          <Badge text={"Health: "+cam.health+"%"} sm color={cam.health>80?T.green:T.accent}/>
          {cam.recording && <Badge text="REC" sm color={T.red}/>}
          {cam.models.slice(0,2).map(function(m){ return <Badge key={m} text={m.split(" ")[0]} xs color={T.teal}/>; })}
        </div>
        <button onClick={onClose}
          style={{marginLeft:16,width:32,height:32,borderRadius:"50%",background:T.faint,border:"1px solid "+T.border,cursor:"pointer",fontSize:16,color:T.muted,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          X
        </button>
      </div>

      {/* Main area */}
      <div style={{flex:1,display:"flex",minHeight:0}}>

        {/* Video feed */}
        <div style={{flex:1,position:"relative",background:"#05080F",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
          <div style={{width:"100%",height:"100%",position:"relative",display:"flex",alignItems:"center",justifyContent:"center",
            background: cam.status==="Offline"
              ? "repeating-linear-gradient(0deg,rgba(255,255,255,.02) 0,rgba(255,255,255,.02) 1px,transparent 1px,transparent 20px)"
              : "linear-gradient(160deg,#0A1628 0%,#0D1F3C 20%,#0A1628 40%,#0C1A30 60%,#0A1628 80%,#0D1F3C 100%)"
          }}>

            {cam.status==="Online" && (
              <>
                <div style={{position:"absolute",bottom:0,left:0,right:0,height:"45%",background:"linear-gradient(to top,#111820,#0A1628)",borderTop:"2px solid rgba(255,255,255,.06)"}}/>
                <div style={{position:"absolute",bottom:"8%",left:"45%",width:"2%",height:"25%",background:"rgba(255,255,255,.07)",borderRadius:2}}/>
                <div style={{position:"absolute",bottom:"8%",left:"52%",width:"2%",height:"25%",background:"rgba(255,255,255,.07)",borderRadius:2}}/>
                <div style={{position:"absolute",top:"54%",left:0,right:0,height:1,background:"rgba(255,255,255,.04)"}}/>
                {detections.map(function(d,i){
                  return (
                    <div key={i} style={{position:"absolute",left:d.x+"%",top:d.y+"%",width:d.w+"%",height:d.h+"%",border:"2px solid "+d.color,borderRadius:3,boxShadow:"0 0 8px "+d.color+"44"}}>
                      <div style={{position:"absolute",top:-18,left:-1,background:d.color,borderRadius:"3px 3px 0 0",padding:"1px 6px",fontSize:9,fontWeight:700,color:"#000",display:"flex",gap:4,alignItems:"center"}}>
                        <span>{d.label}</span>
                        <span style={{opacity:.75}}>{d.conf}%</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{position:"absolute",left:0,right:0,height:2,background:"linear-gradient(90deg,transparent,"+T.blue+"60,transparent)",animation:"scanLine 3s linear infinite",top:0}}/>
                <div style={{position:"absolute",top:12,right:14,fontSize:9,fontFamily:T.mono,color:"rgba(255,255,255,.55)",textAlign:"right",lineHeight:1.7}}>
                  <div>{cam.id}</div>
                  <div>{cam.ip}</div>
                  <div>{new Date().toLocaleTimeString("en-IN")}</div>
                  <div>{cam.res} - {cam.fps}fps</div>
                </div>
                {cam.recording && (
                  <div style={{position:"absolute",top:12,left:14,display:"flex",alignItems:"center",gap:4}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:T.red,display:"block",boxShadow:"0 0 "+(tick%2===0?8:2)+"px "+T.red,transition:"box-shadow .8s"}}/>
                    <span style={{fontSize:9,fontFamily:T.mono,color:T.red,fontWeight:700}}>REC</span>
                  </div>
                )}
                <div style={{position:"absolute",bottom:12,left:14,fontSize:9,fontFamily:T.mono,color:T.teal+"BB",lineHeight:1.6}}>
                  AI: {cam.models.slice(0,2).join(" - ")}
                </div>
                {snapshot && <div style={{position:"absolute",inset:0,background:"rgba(255,255,255,.85)",animation:"flashOut .4s ease-out forwards"}}/>}
              </>
            )}

            {cam.status==="Offline" && (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,opacity:.5}}>
                <span style={{fontSize:56}}>X</span>
                <div style={{fontSize:14,fontWeight:700,color:T.muted}}>Camera Offline</div>
                <div style={{fontSize:11,color:T.muted}}>{cam.ip}</div>
              </div>
            )}

            {cam.status==="Degraded" && (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
                <span style={{fontSize:56}}>!</span>
                <div style={{fontSize:14,fontWeight:700,color:T.accent}}>Signal Degraded</div>
                <div style={{fontSize:11,color:T.muted}}>Partial feed -- {cam.health}% quality</div>
              </div>
            )}

          </div>
        </div>

        {/* Right sidebar */}
        <div style={{width:260,background:T.bg,borderLeft:"1px solid "+T.border,display:"flex",flexDirection:"column",overflowY:"auto",flexShrink:0}}>

          {/* Camera info */}
          <div style={{padding:"14px 16px",borderBottom:"1px solid "+T.border}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:.8,marginBottom:8,textTransform:"uppercase"}}>Camera Info</div>
            {[
              ["Status",  <Dot s={cam.status}/>],
              ["IP",      <span style={{fontFamily:T.mono,fontSize:9}}>{cam.ip}</span>],
              ["Site",    cam.siteId],
              ["Zone",    cam.zone||"--"],
              ["Brand",   cam.brand],
              ["Type",    cam.type],
              ["Res",     cam.res],
              ["FPS",     cam.fps+" fps"],
              ["Health",  <span style={{color:cam.health>80?T.green:T.accent,fontWeight:700}}>{cam.health}%</span>],
              ["Alerts",  <span style={{color:cam.alerts>20?T.red:T.accent,fontWeight:700}}>{cam.alerts}</span>],
              ["Record",  <Badge text={cam.recording?"REC":"OFF"} xs color={cam.recording?T.red:T.muted}/>],
              ["Retention", cam.retention],
            ].map(function(row){ const l=row[0]; const v=row[1]; return (
              <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5,fontSize:10}}>
                <span style={{color:T.muted}}>{l}</span>
                <span style={{color:T.text,fontWeight:600}}>{v}</span>
              </div>
            ); })}
          </div>

          {/* AI Models */}
          <div style={{padding:"12px 16px",borderBottom:"1px solid "+T.border}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:.8,marginBottom:8,textTransform:"uppercase"}}>AI Models Active</div>
            {cam.models.map(function(m){ return (
              <div key={m} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:T.teal,flexShrink:0}}/>
                <span style={{fontSize:10,color:T.text}}>{m}</span>
                <Badge text="Live" xs color={T.teal}/>
              </div>
            ); })}
          </div>

          {/* Recent detections */}
          <div style={{padding:"12px 16px",borderBottom:"1px solid "+T.border}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:.8,marginBottom:8,textTransform:"uppercase"}}>Recent Detections</div>
            {[
              {t:"09:42:11",type:"Signal Violation",sev:"High"},
              {t:"09:38:05",type:"PPE Violation",sev:"High"},
              {t:"09:31:22",type:"Vehicle Speeding",sev:"Medium"},
              {t:"09:18:47",type:"Crowd Detected",sev:"Low"},
              {t:"09:02:03",type:"ANPR Match",sev:"Info"},
            ].map(function(d,i){ return (
              <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",marginBottom:7,padding:"5px 7px",background:T.deep,borderRadius:6,border:"1px solid "+T.border}}>
                <span style={{fontSize:8,color:T.muted,fontFamily:T.mono,marginTop:1,flexShrink:0}}>{d.t}</span>
                <span style={{fontSize:9,color:T.text,flex:1}}>{d.type}</span>
                <Badge text={d.sev} xs color={{High:T.red,Medium:T.accent,Low:T.muted,Info:T.blue}[d.sev]}/>
              </div>
            ); })}
          </div>

          {/* Controls */}
          <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:7}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:.8,marginBottom:2,textTransform:"uppercase"}}>Controls</div>
            <button onClick={function(){ setSnapshot(true); setTimeout(function(){ setSnapshot(false); }, 400); }}
              style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.border,background:T.deep,cursor:"pointer",fontSize:10,color:T.text,fontFamily:T.font,fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
              Snapshot
            </button>
            <button style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.border,background:T.deep,cursor:"pointer",fontSize:10,color:T.text,fontFamily:T.font,fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
              Download Clip
            </button>
            <button style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.border,background:T.deep,cursor:"pointer",fontSize:10,color:T.text,fontFamily:T.font,fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
              Search Timeline
            </button>
            <button style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.border,background:T.deep,cursor:"pointer",fontSize:10,color:T.text,fontFamily:T.font,fontWeight:600,display:"flex",alignItems:"center",gap:7}}>
              Share Live Link
            </button>
            <button onClick={onClose}
              style={{padding:"7px 12px",borderRadius:8,border:"1px solid "+T.red+"44",background:T.red+"12",cursor:"pointer",fontSize:10,color:T.red,fontFamily:T.font,fontWeight:700,display:"flex",alignItems:"center",gap:7,marginTop:4}}>
              Close View
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};
export const getConfig=(k,d=null)=>{try{return JSON.parse(localStorage.getItem(k))||d}catch(e){return d}};
export const setConfig=(k,v)=>{try{localStorage.setItem(k,JSON.stringify(v))}catch(e){}};
export const CONFIG_DEFAULTS_EXPORT={theme:"light",lang:"en"};
export const API="";
